import PDFBuilder, { fmtDate, fmtDateFromISO, formatCI } from "../../utils/pdfBuilder.js";

const tipImobilLabels = {
  apartament: "apartamentul",
  casa: "casa",
  garsoniera: "garsoniera",
  spatiu_comercial: "spatiul comercial",
  birou: "biroul",
  altul: "imobilul",
};

const CONDITION_LABELS = {
  buna: "Buna",
  satisfacatoare: "Satisfacatoare",
  uzura_normala: "Uzura normala",
};

const STARE_LABELS = {
  buna: "Buna",
  satisfacatoare: "Satisfacatoare",
  uzura_normala: "Uzura normala",
};

const UTILITY_DEFS = [
  { label: "Energie electrica", key: "energieElectrica", unit: "kWh" },
  { label: "Gaz natural", key: "gaz", unit: "mc" },
  { label: "Apa calda", key: "apaCalda", unit: "mc" },
  { label: "Apa rece", key: "apaRece", unit: "mc" },
  { label: "Incalzire", key: "incalzire", unit: "Gcal" },
];

export default function buildInchirierePVPDF(data, filename) {
  const pdf = new PDFBuilder();
  const today = fmtDate(new Date());
  const pvDate = data.pvDate ? fmtDateFromISO(data.pvDate) : today;

  const imobilLabel = data.imobilTip === "altul"
    ? (data.imobilTipAltul || "imobilul")
    : (tipImobilLabels[data.imobilTip] || "imobilul");
  const ascundeCamere = data.imobilTip === "spatiu_comercial" || data.imobilTip === "altul";

  const ciPropFmt = data.proprietarCI ? formatCI(data.proprietarCI) : "___ ___";
  const ciChirFmt = data.chiriasCI ? formatCI(data.chiriasCI) : "___ ___";

  const rooms = data.pvRooms || [];
  const utilities = data.pvUtilities || {};
  const hasUtilities = Object.values(utilities).some((v) => v && String(v).trim());
  const hasItems = rooms.some((r) => (r.items || []).length > 0);

  let sectionNr = 3;

  // Header
  pdf.title(
    "Proces Verbal de Predare-Primire",
    `Anexa la Contractul de Inchiriere din data de ${today}`
  );
  pdf.p(`Incheiat astazi, ${pvDate}`, { indent: 0 });
  pdf.space(2);

  // 1. PARTILE
  pdf.section("1. PARTILE");

  pdf.pMixed([
    { text: "Intre " },
    { text: data.proprietarNume || "________________________", bold: true },
    { text: `, CNP ${data.proprietarCNP || "____________"}, CI seria ${ciPropFmt}, domiciliat in ${data.proprietarAdresa || "________________________"}, in calitate de ` },
    { text: "Proprietar", bold: true },
    { text: "," },
  ]);

  pdf.italic("si");

  pdf.pMixed([
    { text: data.chiriasNume || "________________________", bold: true },
    { text: `, CNP ${data.chiriasCNP || "____________"}, CI seria ${ciChirFmt}, domiciliat in ${data.chiriasAdresa || "________________________"}, in calitate de ` },
    { text: "Chirias", bold: true },
    { text: "," },
  ]);

  pdf.intro("s-a procedat la predarea-primirea imobilului in urmatoarele conditii:");

  // 2. IMOBILUL
  pdf.section("2. IMOBILUL");

  const camereText = !ascundeCamere && data.imobilCamere ? `, compus din ${data.imobilCamere} camere` : "";
  const etajText = data.imobilEtaj ? `, etaj ${data.imobilEtaj}` : "";

  pdf.pMixed([
    { text: "Imobilul de tip " },
    { text: imobilLabel, bold: true },
    { text: " situat in " },
    { text: data.imobilAdresa || "________________________", bold: true },
    { text: `${camereText}, suprafata utila ${data.imobilSuprafata || "___"} mp${etajText}, predat ${data.mobilat ? "mobilat si utilat" : "nemobilat"}.` },
  ]);

  const stareLabel = STARE_LABELS[data.pvStareGenerala] || data.pvStareGenerala || "Buna";
  pdf.pMixed([
    { text: "Starea generala a imobilului la momentul predarii: " },
    { text: stareLabel, bold: true },
    { text: "." },
  ]);

  // 3. INVENTAR PE CAMERE
  if (rooms.length > 0) {
    pdf.section(`${sectionNr++}. INVENTAR PE CAMERE`);

    rooms.forEach((room) => {
      // Room heading
      pdf.pMixed([{ text: room.label, bold: true }]);

      const items = room.items || [];
      if (items.length > 0) {
        // Table header row
        pdf.pMixed([
          { text: "Nr.", bold: true },
          { text: "    " },
          { text: "Obiect", bold: true },
          { text: "                              " },
          { text: "Stare", bold: true },
        ], { indent: 5 });

        items.forEach((item, idx) => {
          const condLabel = CONDITION_LABELS[item.condition] || item.condition || "-";
          pdf.pMixed([
            { text: `${idx + 1}.` },
            { text: `  ${item.name || "-"}` },
            { text: ` — ${condLabel}` },
          ], { indent: 5 });
        });
      } else {
        pdf.p("Fara obiecte inventariate.", { indent: 5 });
      }

      if (room.observations) {
        pdf.p(`Observatii: ${room.observations}`, { indent: 5 });
      }

      pdf.space(2);
    });

    if (data.mobilat && !hasItems) {
      pdf.p("Mobilierul si dotarile existente nu sunt inventariate individual.");
    }
  }

  // CITIRI CONTOARE
  if (hasUtilities) {
    pdf.section(`${sectionNr++}. CITIRI CONTOARE`);

    // Table header
    pdf.pMixed([
      { text: "Utilitate", bold: true },
      { text: "                    " },
      { text: "Index", bold: true },
      { text: "          " },
      { text: "U.M.", bold: true },
    ], { indent: 5 });

    UTILITY_DEFS.filter((u) => utilities[u.key]).forEach((u) => {
      pdf.pMixed([
        { text: u.label },
        { text: ": " },
        { text: String(utilities[u.key]), bold: true },
        { text: ` ${u.unit}` },
      ], { indent: 5 });
    });

    pdf.space(2);
  }

  // OBSERVATII GENERALE
  if (data.pvObservatiiGenerale) {
    pdf.section(`${sectionNr++}. OBSERVATII GENERALE`);
    pdf.p(data.pvObservatiiGenerale);
  }

  // DECLARATIE
  pdf.section(`${sectionNr++}. DECLARATIE`);
  pdf.p("Subsemnatii declara ca imobilul a fost predat/primit in starea descrisa mai sus, iar inventarul corespunde situatiei reale de la data predarii. Orice diferente constatate ulterior nu pot fi imputate partii care preda imobilul, daca nu sunt consemnate in prezentul proces-verbal.");

  // Signatures
  pdf.signatures(
    { role: "AM PREDAT — PROPRIETAR", name: data.proprietarNume || "", date: pvDate },
    { role: "AM PRIMIT — CHIRIAS", name: data.chiriasNume || "", date: pvDate },
  );

  pdf.save(filename);
}
